import { useEffect, useState } from "react";

export function compareArraysOfObjects(arr1, arr2) {
  // Check if arrays have different lengths
  if (arr1.length !== arr2.length) {
    return true;
  }

  // Iterate over each object in the first array
  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i];
    const obj2 = arr2[i];

    // Get the keys of the objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if objects have different number of keys
    if (keys1.length !== keys2.length) {
      return true;
    }

    // Iterate over each key in the first object
    for (let j = 0; j < keys1.length; j++) {
      const key = keys1[j];

      // Compare the values of the corresponding keys in both objects
      if (obj1[key] !== obj2[key]) {
        return true;
      }
    }
  }

  // Arrays are identical
  return false;
}

export function compareObjects(obj1, obj2) {
  // Get the keys of both objects
  if (!obj1) {
    return true;
  }
  if (!obj2) {
    return true;
  }
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  // If the number of keys is different, return true
  if (keys1.length !== keys2.length) {
    return true;
  }
  // Iterate over the keys and compare the values
  for (let key of keys1) {
    // If the key is not present in the second object, return true
    // eslint-disable-next-line no-prototype-builtins
    if (!obj2.hasOwnProperty(key)) {
      return true;
    }
    // If the values of the keys are objects, recursively compare them
    if (typeof obj1[key] === "object" && typeof obj2[key] === "object") {
      if (compareObjects(obj1[key], obj2[key])) {
        return true;
      }
    }
    // If the values are not objects and they are not equal, return true
    else if (obj1[key] !== obj2[key]) {
      return true;
    }
  }
  // If all keys and values are the same, return false
  return false;
}

export const convertEpochToUTC = (epochTime) => {
  const date = new Date(epochTime);
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  let result = date.toLocaleString(undefined, options);
  result = result.replace(",", "");
  result = result.replace("/", "-").replace("/", "-");
  result =
    result.substring(6, 10) +
    "-" +
    result.substring(0, 5) +
    result.substring(10, result.length);
  return result;
};

export const formatDateToReadableText = (date) => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(new Date(date));
};
export const formatDateFromTimestamp = (timestamp) => {
  const date = new Date(timestamp);

  // Get year, month, day, hours, minutes, seconds, and milliseconds
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(6, "0"); // Pad to 6 digits

  // Return in the desired format: 'YYYY-MM-DD HH:MM:SS.000000'
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const GraphColors = [
  "#E63946",
  "#EDAE49",
  "#3376BD",
  "#00798C",
  "#52489C",
];

/**
 * Custom hook to debounce a value change. The hook delays updating the `debounceValue` 
 * state until after a specified delay, thus helping to prevent performance issues in 
 * scenarios such as rapidly firing events (like typing in a search field).
 *
 * @param {*} value The value to be debounced. This could be any type 
 * (string, number, object, etc.) depending on the use case.
 * 
 * @param {number} [delay=500] The delay time in milliseconds before the `debounceValue` 
 * state is updated. Defaults to 500ms if not specified.
 *
 * @returns {*} The debounced value. This is the value that will be updated only after 
 * the specified delay. It's of the same type as the input `value`.
 *
 * @example
 // Using the hook in a component
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 *
 * useEffect(() => {
 // API call or other actions to be performed with the debounced value
 * }, [debouncedSearchTerm]);
 */

export const useDebounce = (value, delay = 500) => {
  const [debounceValue, setDebounceValue] = useState(value);

  // useEffect is used so setTimeout can be cleared everytime function is called
  useEffect(() => {
    const timeout = setTimeout(() => {
      // if setTimeout finishes then set debounceValue to the most recent value from useState
      setDebounceValue(value);
    }, delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  // return value thats going to be used for react query/mutation
  return debounceValue;
};

export const saveLayoutToStorage = (layout) => {
  localStorage.setItem("layout", JSON.stringify(layout));
};

/* Create Avatar Color */
/* Create Avatar Color */
export const stringToColor = (string) => {
  let hash = 0;
  let i = 0;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

/* Create Avatar */
export const stringAvatar = (identifier) => {
  if (identifier == null) {
    // Return a default avatar or placeholder
    return {
      sx: {
        bgcolor: "#cccccc",
        color: "#ffffff",
      },
      children: "U", // Placeholder text
    };
  }

  identifier = identifier?.toUpperCase();

  // Function to convert RGB to HSL
  const rgbToHsl = (r, g, b) => {
    ((r /= 255), (g /= 255), (b /= 255));
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);

    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h, s, l];
  };

  // Function to convert HSL to RGB
  const hslToRgb = (h, s, l) => {
    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  };

  const color = stringToColor(identifier);
  let [h, s, l] = rgbToHsl(
    parseInt(color.slice(1, 3), 16),
    parseInt(color.slice(3, 5), 16),
    parseInt(color.slice(5, 7), 16),
  );

  if (l > 0.8) {
    l = 0.8;
  }

  // Adjusting saturation and lightness
  const lighterBackgroundL = Math.min(0.9, l + 0.2); // Increasing lightness for lighter background
  const darkerTextL = Math.max(0.1, l - 0.2); // Decreasing lightness for darker text

  const lighterBackgroundRgb = hslToRgb(h, 0.5 * s, lighterBackgroundL);
  const darkerTextRgb = hslToRgb(h, 0.5 * s, darkerTextL);

  const backgroundHex = `#${lighterBackgroundRgb
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;
  const textHex = `#${darkerTextRgb
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("")}`;

  return {
    sx: {
      bgcolor: backgroundHex,
      color: textHex,
    },
    children: identifier.includes(" ")
      ? `${identifier.split(" ")[0][0]}${identifier.split(" ")[1][0]}`
      : identifier[0],
  };
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
